/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { DatabaseOutlined, FolderOutlined, GoldOutlined } from '@ant-design/icons';
import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import {
  getDivisionNames,
  getMissingDivisionIds,
  isDivisionSubset,
} from '../helpers/divisionHelpers';
import { FULL_EXPORT_ORDER, keyToColumnnMap } from './materials.constants';

export default {};

export const MATERIAL_COST_MAX_DECIMALS = 5;
export const MATERIAL_MARKUP_MAX_DECIMALS = 5;

export const hasMarkup = (markup) => markup || markup === 0;

const roundMarkupPercent = (value) => (
  Number.parseFloat(Number(value).toFixed(MATERIAL_MARKUP_MAX_DECIMALS))
);

export const getMarkupPercent = (markup) => (
  hasMarkup(markup) ? roundMarkupPercent(markup * 100) : markup
);
export const getMarkupString = (markup) => (hasMarkup(markup) ? `${getMarkupPercent(markup)} %` : '');

export const getPriceFloat = (cost, markup, discount = 0) => (
  parseFloat((cost * (1 + (markup / 100)) * (1 - discount)).toFixed(2))
);
export const getPrice = (cost, markup) => `$ ${getPriceFloat(cost, markup).toFixed(2)}`;

export const getMarkupPercentFromPrice = (price, cost) => {
  const costNumber = Number.parseFloat(cost);
  const priceNumber = Number.parseFloat(price);
  if (!costNumber || Number.isNaN(costNumber) || Number.isNaN(priceNumber)) {
    return 0;
  }
  return roundMarkupPercent(((priceNumber / costNumber) - 1) * 100);
};

export const getDerivedPriceValue = (cost, markupDecimal, isDisplay = false) => {
  if (isNullOrUndefined(cost) || !hasMarkup(markupDecimal || 0)) {
    return isDisplay ? '$ 0.00' : '0.00';
  }
  const markupPercent = getMarkupPercent(markupDecimal || 0);
  return isDisplay ? getPrice(cost, markupPercent) : getPriceFloat(cost, markupPercent);
};

export const getUpdatedPricingFields = ({
  changedValues,
  allValues,
  priceKey = 'price',
  markupKey = 'markup',
  costKey = 'cost',
}) => {
  const cost = allValues[costKey];
  const markup = allValues[markupKey] ?? 0;
  // if the price has changed, update the markup
  if (priceKey in changedValues) {
    return {
      [markupKey]: getMarkupPercentFromPrice(allValues[priceKey], cost),
    };
  }
  // if the cost or markup has changed, update the price
  if (
    (costKey in changedValues || markupKey in changedValues)
    && !isNullOrUndefined(cost)
  ) {
    const costNumber = Number.parseFloat(cost);
    const markupNumber = Number.parseFloat(markup);
    if (Number.isFinite(costNumber) && Number.isFinite(markupNumber)) {
      const roundedMarkup = roundMarkupPercent(markupNumber);
      return {
        [priceKey]: getPriceFloat(costNumber, roundedMarkup),
        ...(costKey in changedValues ? { [markupKey]: roundedMarkup } : {}),
      };
    }
  }
  return {};
};

export const changeIsNumber = (change) => (
  change !== null
  && change !== undefined
  && Number.isFinite(change)
);

export const applyCostChange = ({ cost = 0, change, changeMode }) => {
  const currentCost = Number.parseFloat(cost) || 0;
  const newCost = changeMode === 'percentage'
    ? currentCost * (1 + change / 100)
    : currentCost + change;
  return Math.max(0, newCost);
};

export const getValidMarkup = ({ markup = 0, change = 0, isDecimal = false }) => {
  if (isDecimal) {
    return Math.max(-1, markup + change);
  }
  return Math.max(-100, markup + change);
};

export const prepareMaterialLocations = ({
  oldLocations = [],
  newLocations = [],
}) => {
  const oldMap = getIdMap(oldLocations);
  const newMap = getIdMap(newLocations);

  const deletedLocations = [];
  const updatedLocations = [];
  const createdLocations = [];

  oldLocations.forEach((oldLoc) => {
    const {
      id,
      quantity: oldQuant,
      quantityAllocated: oldQuantAlloc,
      equipmentId: oldEqId,
      globalMaterialLocationsId: oldGloLocId,
    } = oldLoc;
    if (!(id in newMap)) {
      deletedLocations.push(id);
    } else {
      const {
        [id]: {
          quantity: newQuant,
          quantityAllocated: newQuantAlloc,
          equipmentId: newEqId,
          globalMaterialLocationsId: newGloLocId,
        } = {},
      } = newMap;

      if (
        oldQuant !== newQuant
        || oldEqId !== newEqId
        || oldGloLocId !== newGloLocId
        || oldQuantAlloc !== newQuantAlloc
      ) {
        updatedLocations.push({
          id,
          quantity: newQuant,
          quantityAllocated: newQuantAlloc,
          equipmentId: newEqId,
          globalMaterialLocationsId: newGloLocId,
        });
      }
    }
  });

  newLocations.forEach((newLoc) => {
    const {
      id, quantity, quantityAllocated, equipmentId, isNew, globalMaterialLocationsId,
    } = newLoc;
    if (
      (!(id in oldMap) || isNew)
      && (equipmentId || globalMaterialLocationsId)
      && (quantity || quantity === 0)
      && (quantityAllocated || quantityAllocated === 0)
    ) {
      createdLocations.push({
        quantity,
        quantityAllocated,
        equipmentId,
        globalMaterialLocationsId,
      });
    }
  }, []);
  return {
    deletedLocations,
    updatedLocations,
    createdLocations,
  };
};

export const getMaterialIcon = (record) => {
  if (record.isFolder) return <FolderOutlined />;
  if (record.isAssembly) return <DatabaseOutlined />;
  return <GoldOutlined />;
};

export const getRelevantDeleteWarning = (
  materials,
  materialIds,
) => {
  const materialIdSet = new Set(materialIds);
  const relevantMaterials = new Set();
  const relevantAssemblyMaterials = Object.values(materials).filter((material) => (
    material.assemblyMaterials?.some((assemblyMaterial) => {
      if (materialIdSet.has(assemblyMaterial.materialId)) {
        relevantMaterials.add(assemblyMaterial.materialId);
        return true;
      }

      return false;
    })
  ));

  return relevantAssemblyMaterials.length > 0
    ? (
      <>
        <div>
          The following materials are used in {relevantAssemblyMaterials.length} assemblies:
        </div>
        <ul>
          {Array.from(relevantMaterials).map((materialId) => (
            <li key={materialId}>{materials[materialId].name}</li>
          ))}
        </ul>
      </>
    )
    : null;
};

export const inCurrentFolder = (material, materialsMap, currentFolderId) => {
  const materialGroupId = material.groupId;

  if (!currentFolderId) return true;

  if (materialGroupId === currentFolderId) return true;

  let current = materialsMap[materialGroupId];
  while (current) {
    if (current.id === currentFolderId) return true;
    current = materialsMap[current.groupId];
  }

  return false;
};

/**
 * Every material that lives under `folderId`, at any depth.
 * @param {string} folderId
 * @param {object} materialsMap id keyed map of every material and folder
 * @returns {Array<object>}
 */
export const getFolderDescendants = (folderId, materialsMap = {}) => {
  if (!folderId) return [];
  return Object.values(materialsMap).filter((material) => (
    material.id !== folderId && inCurrentFolder(material, materialsMap, folderId)
  ));
};

/**
 * Client mirror of the server's folder-division cascade: when a folder loses divisions,
 * strip those divisions from every descendant. A descendant left with none inherits the
 * folder's remaining set, matching `cascadeFolderDivisionRemoval`.
 * @param {object} params.materialsMap id keyed map of every material and folder
 * @param {string} params.folderId
 * @param {Array<string>} params.previousDivisionIds the folder's divisions before the edit
 * @param {Array<string>} params.nextDivisionIds the folder's divisions after the edit
 * @returns {object} a new materials map; unchanged if nothing was removed
 */
export const applyFolderDivisionCascade = ({
  materialsMap = {},
  folderId,
  previousDivisionIds = [],
  nextDivisionIds = [],
}) => {
  const removedDivisionIds = getMissingDivisionIds(previousDivisionIds, nextDivisionIds);
  if (!folderId || !removedDivisionIds.length) return materialsMap;

  const removedDivisionIdSet = new Set(removedDivisionIds);
  const descendants = getFolderDescendants(folderId, materialsMap);
  if (!descendants.length) return materialsMap;

  const nextMap = { ...materialsMap };
  descendants.forEach((descendant) => {
    const currentDivisionIds = descendant.divisionIds ?? [];
    if (!currentDivisionIds.some((divisionId) => removedDivisionIdSet.has(divisionId))) return;

    const kept = currentDivisionIds.filter((divisionId) => !removedDivisionIdSet.has(divisionId));
    const next = kept.length ? kept : nextDivisionIds;
    if (!next.length) return;

    nextMap[descendant.id] = {
      ...descendant,
      divisionIds: next,
      divisionId: next[0],
    };
  });
  return nextMap;
};

/**
 * An assembly may only live in divisions that every one of its components lives in.
 * Returns one entry per component that would block `divisionIds`, naming the divisions
 * the component is missing.
 * @param {Array<string>} params.divisionIds the divisions the assembly wants
 * @param {Array<object>} params.assemblyMaterials the assembly's components
 * @param {object} params.materialsMap id keyed map of every material
 * @param {object} params.divisionMap id keyed map of divisions
 * @returns {Array<{ name: string, missingDivisionNames: Array<string> }>}
 */
export const getComponentsBlockingDivisions = ({
  divisionIds = [],
  assemblyMaterials = [],
  materialsMap = {},
  divisionMap = {},
}) => assemblyMaterials.reduce((acc, { materialId }) => {
  const component = materialId ? materialsMap[materialId] : null;
  if (!component) return acc;

  const missing = getMissingDivisionIds(divisionIds, component.divisionIds);
  if (!missing.length) return acc;

  acc.push({
    name: component.name,
    missingDivisionNames: getDivisionNames(missing, divisionMap),
  });
  return acc;
}, []);

/**
 * The materials that may be used as components of an assembly.
 *
 * An assembly may only live in divisions that every one of its components lives in, so
 * a candidate component's divisions have to cover the assembly's.
 * @param {Array<object>} params.materials every material, as a list
 * @param {Array<string>} params.divisionIds the assembly's divisions
 * @returns {Array<object>}
 */
export const getAssemblyComponentOptions = ({ materials = [], divisionIds = [] }) => {
  if (!divisionIds.length) return [];
  return materials.filter((material) => (
    isDivisionSubset(divisionIds, material.divisionIds)
    && material.active
    && !material.isFolder
    && !material.isAssembly
  ));
};

/**
 * The divisions a material table row should be filtered by.
 *
 * A global material's divisions live on the material itself. A project material is genuinely single
 * division and is not in the global material map, so its pinned `divisionId` is used.
 * @param {object} params.record the table row
 * @param {object} params.materials id keyed map of global materials
 * @param {string} params.formDivisionId the division the form is scoped to
 * @returns {Array<string>}
 */
export const getMaterialRowDivisionIds = ({
  record = {},
  materials = {},
  formDivisionId = null,
}) => {
  const { divisionIds: materialDivisionIds } = materials[record.materialId] ?? {};
  if (materialDivisionIds?.length) return materialDivisionIds;
  if (record.divisionId) return [record.divisionId];
  return formDivisionId ? [formDivisionId] : [];
};

export const getMaterialPath = (material, materials) => {
  let current = material;
  const pathArray = [];
  const crumbs = [];

  while (current.groupId) {
    const parent = materials[current.groupId];
    if (!parent) break;
    pathArray.unshift(parent.name);
    crumbs.unshift({ title: parent.name, groupId: parent.id });
    current = parent;
  }

  const fullPath = pathArray.length > 0 ? pathArray.join(' / ') : null;

  return { fullPath, crumbs };
};

export const formatExportedMaterial = ({
  data,
  selectedColumns,
  costcodeMap,
  equipmentMap,
  globalMaterialLocationsMap,
  vendorMap,
}) => {
  const exportColumns = FULL_EXPORT_ORDER.filter((key) => ['name', 'groupId', 'active'].includes(key) || selectedColumns?.has?.(key));

  return data
    .map((item) => {
      const filteredItem = exportColumns.reduce((acc, key) => {
        if (key === 'groupId') {
          acc.Folder = item?.fullPath ?? '';
        } else if (key === 'active') {
          const status = item[key];
          acc.Status = status ? 'Active' : 'Archived';
        } else if (key === 'cost') {
          acc['Material Cost ($)'] = item?.cost || '';
        } else if (key === 'labourCost') {
          acc['Labour Cost ($)'] = item?.labourCost || '';
        } else if (key === 'price') {
          const { cost } = item;
          const { markup } = item;
          acc['Material Price ($)'] = hasMarkup(markup) && cost ? (cost * (1 + markup)).toFixed(2) : '';
        } else if (key === 'markup') {
          acc['Material Markup (%)'] = hasMarkup(item?.markup) && item?.cost ? item.markup * 100 : '';
        } else if (key === 'labourMarkup') {
          acc['Labour Markup (%)'] = hasMarkup(item?.labourMarkup) && item?.labourCost ? item.labourMarkup * 100 : '';
        } else if (key === 'labourPrice') {
          const { labourCost } = item;
          const { labourMarkup } = item;
          acc['Labour Price ($)'] = hasMarkup(labourMarkup) && labourCost ? (labourCost * (1 + labourMarkup)).toFixed(2) : '';
        } else if (key === 'quantityAvailable') {
          const quantity = item.quantity ?? 0;
          const quantityAllocated = item.quantityAllocated ?? 0;
          acc['Quantity Available'] = quantity - quantityAllocated;
        } else if (key === 'costcode') {
          const costcode = costcodeMap?.[item.costcodeId];
          acc['Cost Code'] = costcode?.name ?? '';
        } else if (key === 'vendor') {
          const vendor = vendorMap?.[item.vendorId];
          acc.Vendor = vendor?.name ?? '';
        } else if (key === 'locations') {
          const { locations } = item;
          const locationNames = locations.map((location) => {
            const { globalMaterialLocationsId, equipmentId } = location;

            const {
              [equipmentId]: {
                name,
              } = {},
            } = equipmentMap;

            const {
              [globalMaterialLocationsId]: {
                locationText: globalMaterialLocationsText,
              } = {},
            } = globalMaterialLocationsMap;

            return name || globalMaterialLocationsText;
          }).join(', ');

          acc.Locations = locationNames;
        } else {
          acc[keyToColumnnMap[key]] = item[key] ?? null;
        }
        return acc;
      }, {});
      return filteredItem;
    });
};

export const isQuantityValid = (quantity) => (
  !isNullOrUndefined(quantity) && !Number.isNaN(parseFloat(quantity))
);

/**
 * Live: Quantity Remaining = Quantity Allocated − Quantity (null/invalid quantity counts as 0).
 * Static: preserve mapped/stored quantityRemaining.
 */
export const computeQuantityRemaining = ({
  quantity,
  quantityAllocated,
  quantityRemaining,
  quantityRemainingMode = 'live',
}) => {
  if (quantityRemainingMode === 'static') {
    return quantityRemaining;
  }
  if (!isQuantityValid(quantityAllocated)) {
    return undefined;
  }
  const usedQuantity = isQuantityValid(quantity) ? parseFloat(quantity) : 0;
  return parseFloat((parseFloat(quantityAllocated) - usedQuantity).toFixed(2));
};

/**
 * Live: quantity is already subtracted from the allocation, so a negative remainder is an overage.
 * Static: the remainder is a mapped value that quantity is not subtracted from,
 * so compare the entered quantity against it directly.
 */
export const isQuantityOverRemaining = ({
  quantity,
  quantityRemaining,
  quantityRemainingMode = 'live',
}) => {
  if (!isQuantityValid(quantityRemaining)) return false;

  if (quantityRemainingMode === 'static') {
    // Empty quantity counts as 0 so a negative mapped remainder still warns
    const usedQuantity = isQuantityValid(quantity) ? parseFloat(quantity) : 0;
    return usedQuantity > parseFloat(quantityRemaining);
  }

  return parseFloat(quantityRemaining) < 0;
};
